import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Lock } from 'lucide-react';
import useAppStore from '../store/appStore'; 
import useAdminStore from '../store/adminStore';
import ConfirmationModal from '../components/ConfirmationModal';

import getAppPages, { AppIconMap } from '../config/pageRegistry';
import { isSoloModeActive } from '../utils/soloMode';
import { ClientWelcomeHeader } from '../components/ClientWelcomeHeader';
import { auth } from '../config/firebase';
import {
  isPagePinLockedForUi,
  normalizePinLockConfig,
  PIN_SESSION_EVENT,
} from '../utils/pinLockSession';

// ============================================================================
// STYLES: STARTSEITE CARBON CORE INJECTION
// ============================================================================
const StartseiteStyles = () => (
    <style>{`
        .carbon-weave-bg {
            background-color: #0a0b0e;
            background-image: 
                radial-gradient(circle at 100% 150%, #08090c 24%, #14161f 24%, #14161f 28%, #08090c 28%, #08090c 36%, #14161f 36%, #14161f 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #08090c 24%, #14161f 24%, #14161f 28%, #08090c 28%, #08090c 36%, #14161f 36%, #14161f 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #14161f 10%, #08090c 10%, #08090c 23%, #14161f 23%, #14161f 30%, #08090c 30%, #08090c 43%, #14161f 43%, #14161f 50%, #08090c 50%, #08090c 63%, #14161f 63%, #14161f 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #14161f 5%, #08090c 5%, #08090c 15%, #14161f 15%, #14161f 20%, #08090c 20%, #08090c 29%, #14161f 29%, #14161f 34%, #08090c 34%, #08090c 44%, #14161f 44%, #14161f 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #14161f 5%, #08090c 5%, #08090c 15%, #14161f 15%, #14161f 20%, #08090c 20%, #08090c 29%, #14161f 29%, #14161f 34%, #08090c 34%, #08090c 44%, #14161f 44%, #14161f 49%, transparent 49%, transparent);
            background-size: 16px 16px;
        }

        .carbon-panel {
            position: relative;
            border: 1px solid #20232b;
            box-shadow: 0 15px 35px -10px rgba(0,0,0,0.9), inset 0 1px 2px rgba(255,255,255,0.05);
        }

        .carbon-panel::after {
            content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.4)); border-radius: inherit; pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }
        
        .led-gold { color: #E6C299; text-shadow: 0 0 10px rgba(230,194,153,0.6); }
        .led-red { color: #ef4444; text-shadow: 0 0 10px rgba(239,68,68,0.6); }
        
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(230, 194, 153, 0.3); border-radius: 10px; }

        .premium-watermark {
            position: absolute;
            pointer-events: none;
            user-select: none;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.25em;
            line-height: 1;
            opacity: 0.32;
            background: linear-gradient(135deg, #ef4444 0%, #E6C299 45%, #b48a56 100%);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            z-index: 5;
        }

        .premium-card-glass {
            backdrop-filter: blur(12px);
            background: rgba(230, 194, 153, 0.08);
            border-color: rgba(230, 194, 153, 0.28);
            box-shadow: 0 0 20px rgba(230, 194, 153, 0.06), inset 0 1px 2px rgba(255,255,255,0.04);
        }

        /* Light Mode: Carbon → Warm-Platin (Dark-Regeln oben bleiben unverändert) */
        html:not(.dark) .carbon-weave-bg {
            background-image: none;
            background-color: var(--pe-bg-elevated);
        }
        html:not(.dark) .carbon-panel {
            border: 1px solid var(--pe-border-subtle);
            box-shadow: 0 12px 28px -12px rgba(28, 25, 23, 0.18), inset 0 1px 2px rgba(255,255,255,0.6);
        }
        html:not(.dark) .carbon-panel::after {
            background: none;
        }
        html:not(.dark) .led-gold { color: var(--pe-accent-text); text-shadow: none; }
        html:not(.dark) .led-red { color: #B91C1C; text-shadow: none; }
        html:not(.dark) .custom-scrollbar::-webkit-scrollbar-track { background: rgba(28, 25, 23, 0.08); }
        html:not(.dark) .premium-card-glass {
            background: rgba(230, 194, 153, 0.12);
            border-color: rgba(141, 100, 53, 0.35);
            box-shadow: 0 2px 10px rgba(28, 25, 23, 0.06);
        }
    `}</style>
);

const ArrowRight = ({ className = "" }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);

const SimpleCard = ({ title, desc, onClick, colorClass, iconStr, isPremium, isPinLocked }: any) => {
    const IconComponent = AppIconMap[iconStr] || AppIconMap.help; 

    return (
        <button 
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            className={`
                w-full text-left p-6 rounded-lg text-pe-text border
                border-l-4 ${colorClass}
                hover:border-[#E6C299]/40 hover:bg-pe-hover dark:hover:bg-slate-900/30 transition-all duration-200 shadow-md
                mb-2 relative overflow-hidden group h-full flex items-start gap-4 carbon-panel
                ${isPremium ? 'premium-card-glass' : 'bg-pe-card dark:bg-black/40 border-pe-border dark:border-[#22252e]'}
            `}
        >
            {isPremium && (
                <span aria-hidden className="premium-watermark right-4 bottom-3 text-base md:text-lg">
                    Premium
                </span>
            )}

            {isPinLocked && (
                <span
                    aria-label="Mit Master-PIN gesperrt"
                    className="absolute top-3 right-3 z-20 flex items-center justify-center w-7 h-7 rounded-md bg-black/30 border border-amber-400/20"
                >
                    <Lock className="w-3.5 h-3.5 text-amber-800 dark:text-amber-400/80" strokeWidth={2.5} />
                </span>
            )}

            <div className={`flex-shrink-0 p-3 rounded-lg bg-black/5 group-hover:bg-black/10 dark:bg-white/5 dark:group-hover:bg-white/10 transition-colors ${colorClass.replace('border-', 'text-')}`}>
                <IconComponent size={24} strokeWidth={2} />
            </div>

            <div className="flex-1 carbon-content">
                <div className="font-bold text-lg mb-1 group-hover:text-[#E6C299] transition-colors font-sans">{title}</div>
                <div className="text-pe-caption text-pe-muted leading-relaxed font-sans">{desc}</div>
            </div>

            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0 text-pe-accent-text led-gold z-10">
                <ArrowRight />
            </div>
        </button>
    );
};

interface StartseiteProps {
    pages?: any[]; 
    navigateTo: (pageKey: string) => void;
    showNotification?: (msg: string) => void;
    currentYearStatus?: string;
    currentYear?: string;
}

const Startseite: React.FC<StartseiteProps> = ({
    navigateTo,
    showNotification,
    currentYearStatus: yearStatusProp,
    currentYear: yearProp,
}) => {
    
    const { 
        clientData, 
        settings, 
        setClientData, 
        initializeStore, 
        isInitialized,
        createSnapshot,
        unlockedPremiumTools,
    } = useAppStore((state: any) => ({
        clientData: state.clientData,
        settings: state.settings,
        setClientData: state.setClientData,
        initializeStore: state.initializeStore,
        isInitialized: state.isInitialized,
        createSnapshot: state.createSnapshot,
        unlockedPremiumTools: state.unlockedPremiumTools,
    }));

    const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
    const isGodMode =
        !!impersonatedClientId ||
        !!sessionStorage.getItem('GOD_MODE_ID') ||
        !!(clientData as any)?._isGodMode;
    const mandantUid = impersonatedClientId || auth.currentUser?.uid || '';
    const pinLockConfig = normalizePinLockConfig((clientData as any)?.pinLockConfig);
    const [pinSessionTick, setPinSessionTick] = useState(0);

    useEffect(() => {
        const onChange = () => setPinSessionTick((t) => t + 1);
        window.addEventListener(PIN_SESSION_EVENT, onChange);
        return () => window.removeEventListener(PIN_SESSION_EVENT, onChange);
    }, []);

    useEffect(() => {
        if (!isInitialized) {
            initializeStore();
        }
    }, [isInitialized, initializeStore]);

    const rawEmployees = clientData?.personalData || [];
    const soloMode = isSoloModeActive(clientData);
    const isBasicMode = clientData?.lizenzDetails?.typ === 'basic';

    const currentYearStatus =
        yearStatusProp ||
        (typeof window !== 'undefined' ? localStorage.getItem('CURRENT_YEAR_STATUS') || 'aktiv' : 'aktiv');
    const isDatenerfassung =
        currentYearStatus === 'datenerfassung' || currentYearStatus === 'angefragt';
    const isAbgeschlossen = currentYearStatus === 'abgeschlossen';
    const yearSignal = yearProp || (typeof window !== 'undefined' ? localStorage.getItem('CURRENT_YEAR') || '' : '');

    const [password, setPassword] = useState('');
    const [isPasswordInputVisible, setIsPasswordInputVisible] = useState(false);
    const [isAdminMenuVisible, setIsAdminMenuVisible] = useState(false);
    // God-Mode/Admin-PIN: Mitarbeiterliste nie durch Solo leeren (Henne-Ei: Team erst nach Freigabe)
    const displayEmployees = (isAdminMenuVisible || !soloMode)
        ? rawEmployees.filter((p: any) => p)
        : [];
    
    const [isSafetyCheckOpen, setIsSafetyCheckOpen] = useState(false);
    const [isDoubleCheckOpen, setIsDoubleCheckOpen] = useState(false);
    const [isTeamSelectorOpen, setIsTeamSelectorOpen] = useState(false); 
    const [error, setError] = useState('');
    
    const passwordInputRef = useRef<HTMLInputElement>(null);

    const effectiveUnlockedPremiumTools = useMemo(() => {
        const out: string[] = [];
        for (const list of [unlockedPremiumTools, clientData?.unlockedPremiumTools]) {
            if (!Array.isArray(list)) continue;
            for (const id of list) {
                if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
            }
        }
        return out;
    }, [unlockedPremiumTools, clientData?.unlockedPremiumTools]);

    const pages = useMemo(() => {
        // PIN-Bereich offen = volle Toggle-Kontrolle (Coach/Admin/wer das Passwort hat)
        const toggleClientData = {
            ...clientData,
            _forcedYearStatus: currentYearStatus,
            unlockedPremiumTools: effectiveUnlockedPremiumTools,
            ...(isAdminMenuVisible ? { _adminToggleOverride: true } : {}),
        };
        // @ts-ignore
        const result = getAppPages(toggleClientData, settings, navigateTo);
        return Array.isArray(result) ? result : [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientData, settings, navigateTo, currentYearStatus, yearSignal, isAdminMenuVisible, effectiveUnlockedPremiumTools]);

    /** Karten-Matrix = exakt Mandanten-Sicht (nur isVisible) */
    const viewSections = useMemo(() => {
        const sections: Record<string, any[]> = {
            verwaltung: [],
            basis: [],
            organisation: [],
            daten: [],
            tools: [],
            auswertung: [],
            analyse: [],
            abschluss: []
        };

        pages.forEach((p: any) => {
            if (!p || p.key === 'startseite') return;
            if (p.key.startsWith('leistung-')) return;
            if (!p.isVisible) return;
            if (
                (p.key === 'pe-papers' ||
                    p.key === 'pe-profit' ||
                    p.key === 'pe-start' ||
                    p.key === 'pe-rent' ||
                    p.key === 'pe-plan' ||
                    p.key === 'pe-scout' ||
                    p.key === 'pe-facts') &&
                !effectiveUnlockedPremiumTools.includes(p.key)
            ) {
                return;
            }
            // Intern-Tools nur Gott-Modus
            if ((p.key === 'planungsrechner' || p.key === 'pe-solo') && !isGodMode) {
                return;
            }

            const group = p.group || 'basis';
            if (sections[group]) {
                sections[group].push(p);
            }
        });

        // Premium-Tools ans Ende der Gruppe
        sections.verwaltung.sort(
            (a, b) => Number(!!a.isPremium) - Number(!!b.isPremium)
        );
        sections.analyse.sort(
            (a, b) => Number(!!a.isPremium) - Number(!!b.isPremium)
        );

        return sections;
    }, [pages, effectiveUnlockedPremiumTools, isGodMode]);

    /** Admin-PIN-Katalog = ALLE Schalter (unabhängig von Mandanten-Sicht) */
    const adminCatalogSections = useMemo(() => {
        const sections: Record<string, any[]> = {
            verwaltung: [],
            basis: [],
            organisation: [],
            daten: [],
            tools: [],
            auswertung: [],
            analyse: [],
            abschluss: []
        };

        pages.forEach((p: any) => {
            if (!p || p.key === 'startseite') return;
            // Gott-Modus-only: nicht im Mandanten-Katalog verwalten
            if (p.key === 'coach-akte' || p.key === 'planungsrechner' || p.key === 'pe-solo') return;
            if (p.key.startsWith('leistung-')) return;

            const group = p.group || 'basis';
            if (sections[group]) {
                sections[group].push(p);
            }
        });

        sections.verwaltung.sort(
            (a, b) => Number(!!a.isPremium) - Number(!!b.isPremium)
        );
        sections.analyse.sort(
            (a, b) => Number(!!a.isPremium) - Number(!!b.isPremium)
        );

        return sections;
    }, [pages]);

    const customerViewPages = useMemo(() => {
        return pages.filter((p: any) => p && p.group === 'daten').map((p: any) => p.key);
    }, [pages]);

    const areAnyDataPagesVisible = customerViewPages.some((key) => {
        const page = pages.find((p: any) => p.key === key);
        return page ? page.isVisible : false;
    });

    useEffect(() => {
        if (isPasswordInputVisible) setTimeout(() => passwordInputRef.current?.focus(), 100);
    }, [isPasswordInputVisible]);

    const handleAdminClick = () => {
        if (isAdminMenuVisible) {
            setIsAdminMenuVisible(false);
            setIsPasswordInputVisible(false);
            setPassword('');
            setError('');
        } else {
            setIsPasswordInputVisible(true);
        }
    };

    const checkPassword = () => {
        if (password === '90') {
            setIsAdminMenuVisible(true);
            setIsPasswordInputVisible(false);
            setPassword('');
            setError('');
        } else {
            setError('Falsch');
            if(showNotification) showNotification('Falsches Passwort');
        }
    };

    const handleToggleArbeitsblaetterClick = () => {
        if (areAnyDataPagesVisible) {
            setIsSafetyCheckOpen(true);
        } else {
            performVisibilityToggle(true); 
        }
    };

    const performVisibilityToggle = (makeVisible: boolean) => {
        if (createSnapshot) {
            const label = makeVisible ? "Auto-Backup: Bearbeitung (Sichtbar)" : "Auto-Backup: Präsentation (Versteckt)";
            createSnapshot(label);
            if(showNotification) showNotification(`Sicherung erstellt: ${label}`);
        }

        setClientData((prev: any) => {
            if (!prev) return prev;
            const newPageVisibility = { ...(prev.pageVisibility || {}) };
            customerViewPages.forEach(key => { 
                const storageKey = isDatenerfassung ? `${key}_datenerfassung` : 
                                   isAbgeschlossen ? `${key}_abgeschlossen` : 
                                   key;
                newPageVisibility[storageKey] = makeVisible; 
            });
            return { ...prev, pageVisibility: newPageVisibility };
        });
    };

    const resolveVisibilityStorageKey = (pageKey: string) =>
        isDatenerfassung
            ? `${pageKey}_datenerfassung`
            : isAbgeschlossen
              ? `${pageKey}_abgeschlossen`
              : pageKey;

    /**
     * Freigabe-Toggle schreibt pageVisibility.
     * Anzeige = effektive Sichtbarkeit (isVisible), damit Häkchen = „Modul ist offen“.
     * Basis für den Flip: aktuelles isVisible (nicht nur gespeicherter Map-Wert).
     */
    const handleVisibilityChange = (pageKey: string) => {
        const targetPage = pages.find((p: any) => p.key === pageKey);
        const currentlyVisible = !!(targetPage && targetPage.isVisible);
        const storageKey = resolveVisibilityStorageKey(pageKey);

        setClientData((prev: any) => {
            if (!prev) return prev;
            return {
                ...prev,
                pageVisibility: {
                    ...(prev.pageVisibility || {}),
                    [storageKey]: !currentlyVisible,
                },
            };
        });
    };

    const handleEmployeeCardClick = () => {
        setIsTeamSelectorOpen(true);
    };

    const getPageBorderColor = (page: any) => {
        if (page?.color) return page.color.replace('bg-', 'border-');
        return 'border-blue-500';
    };

    const renderSection = (title: string, groupKey: string, extraCard?: React.ReactNode) => {
        const sectionPages = viewSections[groupKey] || [];
        
        if (sectionPages.length === 0 && !extraCard) return null;

        return (
            <div className="mb-10">
                <h3 className="text-pe-section-size text-pe-section font-semibold uppercase mb-4 pb-2 border-b border-pe-border dark:border-[#22252e] tracking-widest font-sans">
                    {title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {extraCard}
                    {sectionPages.map((page: any) => (
                        <SimpleCard 
                            key={page.key} 
                            title={page.label} 
                            desc={page.description} 
                            onClick={() => navigateTo(page.key)} 
                            colorClass={getPageBorderColor(page)} 
                            iconStr={page.iconStr}
                            isPremium={page.isPremium}
                            isPinLocked={
                                pinSessionTick >= 0 &&
                                isPagePinLockedForUi(page.key, pinLockConfig, mandantUid, isGodMode)
                            }
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full pb-20 relative selection:bg-[#E6C299] selection:text-black">
            <StartseiteStyles />
            
            <ConfirmationModal 
                isOpen={isSafetyCheckOpen} 
                onConfirm={() => { 
                    setIsSafetyCheckOpen(false); 
                    if(showNotification) showNotification("Bitte Kostendeckung prüfen."); 
                    navigateTo('lohnfaktor'); 
                }} 
                onClose={() => { 
                    setIsSafetyCheckOpen(false); 
                    setIsDoubleCheckOpen(true); 
                }} 
                title="1. Sicherheits-Check" 
                message={
                    <div className="text-center">
                        <p className="text-pe-text text-lg font-bold mb-2">Änderungen eingearbeitet?</p>
                        <p className="text-pe-muted dark:text-gray-300">Wurde der <span className="text-blue-700 dark:text-blue-400 font-bold underline">Kostenfaktor</span> entsprechend angepasst?</p>
                    </div>
                } 
                confirmText="NEIN (Erst Prüfen)" 
                cancelText="JA (Weiter)"
                requireCheckbox={true}
                checkboxLabel="Ich bestätige: Kostenfaktor wurde geprüft"
            />
            
            <ConfirmationModal 
                isOpen={isDoubleCheckOpen} 
                onConfirm={() => setIsDoubleCheckOpen(false)} 
                onClose={() => { 
                    setIsDoubleCheckOpen(false); 
                    performVisibilityToggle(false); 
                    if(showNotification) showNotification("Präsentationsmodus aktiv."); 
                }} 
                title="2. Bestätigung" 
                message={
                    <div className="text-center">
                        <p className="text-pe-accent-text text-xl font-bold mb-2">Wirklich ausblenden?</p>
                        <p className="text-pe-muted dark:text-gray-400 italic">Die Arbeitsblätter werden nun für Mandanten versteckt.</p>
                    </div>
                } 
                confirmText="ZURÜCK" 
                cancelText="JA, AUSFÜHREN"
                requireCheckbox={true}
                checkboxLabel="Ich bin mir sicher und möchte fortfahren"
            />

            {isTeamSelectorOpen && (
                <div className="fixed inset-0 bg-stone-900/50 dark:bg-black/90 flex justify-center items-center backdrop-blur-sm" style={{zIndex: 10000}} onClick={() => setIsTeamSelectorOpen(false)}>
                    <div className="bg-pe-elevated dark:bg-[#0a0b0e] border border-pe-border dark:border-[#20232b] carbon-panel carbon-weave-bg rounded-xl p-6 w-full max-w-md m-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6 border-b border-pe-border dark:border-[#22252e] pb-4 carbon-content">
                            <div>
                                <h3 className="text-xl font-bold text-pe-text font-sans">Mitarbeiter wählen</h3>
                                <p className="text-xs text-pe-muted dark:text-slate-400 font-sans">Produktives Personal</p>
                            </div>
                            <button onClick={() => setIsTeamSelectorOpen(false)} className="bg-pe-sunken dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-text w-8 h-8 rounded-full flex items-center justify-center transition-colors text-lg">&times;</button>
                        </div>
                        
                        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar carbon-content">
                            {displayEmployees.map((ma: any, idx: number) => {
                                const isVisible = pages.find((p: any) => p.key === `leistung-${ma.id}`)?.isVisible;
                                if (!ma || (!isVisible && !isAdminMenuVisible)) return null;
                                return (
                                    <button 
                                        key={ma.id || idx} 
                                        type="button"
                                        onClick={() => { setIsTeamSelectorOpen(false); if(ma.id) navigateTo(`leistung-${ma.id}`); }} 
                                        className="w-full flex items-center justify-between p-4 bg-pe-card dark:bg-black/40 hover:bg-pe-hover dark:hover:bg-slate-900/30 rounded border border-pe-border dark:border-[#22252e] border-l-4 border-[#CC8A84] text-pe-text transition-all group"
                                    >
                                        <div className="flex flex-col text-left">
                                            <span className="font-bold text-lg group-hover:text-pe-accent-text transition-colors font-sans">{ma.name || 'Unbenannt'}</span>
                                            <span className="text-[10px] text-stone-500 dark:text-slate-500 uppercase tracking-widest font-sans font-medium">Zur Leistungsakte</span>
                                        </div>
                                        <div className="text-stone-500 dark:text-slate-500 group-hover:text-pe-accent-text group-hover:scale-110 transition-all"><ArrowRight /></div>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-5xl mx-auto pt-6 px-4">

                <ClientWelcomeHeader
                    showNotification={showNotification}
                    subtitle="Alle Module und Fachbereiche im strukturellen Direktzugriff."
                />
                
                {/* --- CENTRAL CONTROL HERO CONTAINER (CARBON RECONSTRUCTION) --- */}
                <div className="bg-pe-elevated dark:bg-[#0a0b0e] p-8 rounded-xl border border-pe-border dark:border-[#20232b] shadow-lg text-center mb-8 relative overflow-hidden carbon-panel carbon-weave-bg">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#E6C299]/30 to-transparent carbon-content"></div>
                    <div className="carbon-content">
                        <h2 className="text-2xl font-black text-pe-text mb-2 uppercase tracking-wide font-sans">Zentrale</h2>
                        {(pages.find((p: any) => p.key === 'info')?.isVisible !== false || isAdminMenuVisible) && (
                            <button onClick={() => navigateTo('info')} className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 text-red-800 dark:text-white px-6 py-3 rounded hover:bg-red-100 dark:hover:bg-red-900/30 inline-flex items-center gap-2 transition-all font-bold text-sm uppercase tracking-wider shadow-md">
                                <span>Empfohlen: Einleitung & Infos</span> <ArrowRight className="led-gold" />
                            </button>
                        )}
                    </div>
                </div>

                {/* --- MAIN MATRIX BACKGROUND --- */}
                <div className="bg-pe-sunken dark:bg-black/20 p-6 rounded-xl border border-pe-border dark:border-[#1f222a] shadow-inner">
                    
                    {renderSection("Verwaltung & Mandat", "verwaltung")}
                    {renderSection("Grundlagen", "basis")}
                    {renderSection("Organisation", "organisation")}
                    {renderSection("Meine Arbeitsdaten", "daten")}
                    {renderSection("Werkzeuge & Tools", "tools")}
                    
                    {renderSection("Auswertungen", "auswertung", 
                        (!isBasicMode && !isDatenerfassung && !soloMode) ? (
                            <SimpleCard 
                                key="group-employee-fixed"
                                title="Mitarbeiter Leistungen" 
                                desc={`Leistungsdaten verwalten (${displayEmployees.length} Personen)`} 
                                onClick={handleEmployeeCardClick} 
                                colorClass="border-[#CC8A84]" 
                                iconStr="users"
                            />
                        ) : undefined
                    )}

                    {renderSection("Analyse Tools", "analyse")}
                    {renderSection("Abschluss", "abschluss")}

                </div>

                {/* =========================================================
                    🔴 ADMIN AREA: SYSTEM LOCK / UNTOUCHED DESIGN ACCORDING TO PROTOCOL
                   ========================================================= */}
                <div className="mt-8 pt-4 pb-12">
                    <div className="flex flex-col items-start">
                        <button onClick={handleAdminClick} className={`text-sm hover:text-pe-text underline mb-2 ${isAdminMenuVisible ? 'text-pe-accent-text font-bold' : 'text-gray-500'}`}>
                            {isAdminMenuVisible ? 'Admin-Bereich schließen' : 'Admin-Bereich öffnen'}
                        </button>

                        {isPasswordInputVisible && !isAdminMenuVisible && (
                            <div className="bg-pe-card dark:bg-[#1a1c24] border border-[#E6C299]/50 p-4 rounded-lg shadow-xl flex flex-col gap-2">
                                <label className="text-pe-accent-text text-xs font-bold uppercase">Admin PIN</label>
                                <div className="flex gap-2">
                                    <input ref={passwordInputRef} type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && checkPassword()} className="bg-pe-input dark:bg-black text-pe-text text-center p-2 rounded border border-pe-border-strong dark:border-gray-700 w-24 outline-none" placeholder="PIN" />
                                    <button onClick={checkPassword} className="bg-pe-accent text-black font-bold px-3 rounded">OK</button>
                                </div>
                                {error && <span className="text-red-500 text-xs">{error}</span>}
                            </div>
                        )}
                    </div>

                    {isAdminMenuVisible && (
                        <div className="mt-4 p-4 bg-blue-900/10 border border-blue-800/30 rounded-xl">
                            <h4 className="text-blue-700 dark:text-blue-400 font-bold mb-4 border-b border-blue-800/30 pb-2 uppercase text-xs tracking-widest">Sichtbarkeit & Module ({currentYearStatus.toUpperCase()})</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                                <div className="bg-pe-card dark:bg-[#15171e] p-3 rounded border border-pe-border dark:border-gray-700">
                                    <h5 className="text-[#9C544D] dark:text-[#CC8A84] text-xs font-bold uppercase mb-2">Personal & Team</h5>
                                    <div className="max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                        {displayEmployees.map((ma: any, idx: number) => {
                                            const pageKey = `leistung-${ma.id}`;
                                            const isVisible = pages.find((p: any) => p.key === pageKey)?.isVisible;
                                            return (
                                                <label key={ma.id || idx} className="flex items-center space-x-2 text-pe-muted dark:text-gray-400 cursor-pointer p-1 hover:bg-pe-hover rounded">
                                                    <input type="checkbox" checked={!!isVisible} onChange={() => handleVisibilityChange(pageKey)} />
                                                    <span className="text-sm">{ma.name || 'Unbenannt'}</span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                </div>
                                
                                {Object.keys(adminCatalogSections).map(groupName => {
                                    const groupPages = adminCatalogSections[groupName];
                                    if (groupPages.length === 0) return null;

                                    const groupColors: Record<string, string> = {
                                        verwaltung: 'text-purple-700 dark:text-purple-400', basis: 'text-pe-muted dark:text-slate-500', organisation: 'text-sky-700 dark:text-sky-400',
                                        daten: 'text-blue-700 dark:text-blue-500', tools: 'text-emerald-700 dark:text-emerald-500', auswertung: 'text-[#9C544D] dark:text-[#CC8A84]',
                                        analyse: 'text-pe-accent-text', abschluss: 'text-pe-accent-text'
                                    };
                                    
                                    const headerColor = groupColors[groupName] || 'text-gray-500';
                                    
                                    const displayName = 
                                        groupName === 'verwaltung' ? 'Verwaltung & Verträge' :
                                        groupName === 'tools' ? 'Arbeitstools' : 
                                        groupName === 'daten' ? 'Arbeitsdaten' : 
                                        groupName === 'auswertung' ? 'Auswertungen' :
                                        groupName === 'analyse' ? 'Analyse Tools' : 
                                        groupName === 'organisation' ? 'Organisation' : 
                                        groupName;
                                    
                                    return (
                                        <div key={groupName} className="bg-pe-card dark:bg-[#15171e] p-3 rounded border border-pe-border dark:border-gray-700">
                                            <h5 className={`${headerColor} text-xs font-bold uppercase mb-2`}>{displayName}</h5>
                                            <div className="flex flex-col gap-2">
                                                {groupPages.map((page: any) => {
                                                    const isVisible = pages.find((p: any) => p.key === page.key)?.isVisible;
                                                    return (
                                                        <label key={page.key} className="flex items-center space-x-2 text-pe-muted dark:text-gray-300 cursor-pointer p-1 hover:bg-pe-hover rounded">
                                                            <input type="checkbox" checked={!!isVisible} onChange={() => handleVisibilityChange(page.key)} />
                                                            <span className="text-sm">{page.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <hr className="border-pe-border dark:border-gray-700 my-4" />
                            <div className="flex flex-col gap-2">
                                <span className="text-blue-700 dark:text-blue-400 text-xs uppercase font-bold tracking-widest">Globale Steuerung (Mandanten-Sicht)</span>
                                <button 
                                    onClick={handleToggleArbeitsblaetterClick} 
                                    className={`w-full py-3 font-black tracking-widest rounded-lg text-sm uppercase transition-all shadow-lg border ${areAnyDataPagesVisible ? 'bg-blue-600 dark:bg-blue-900/50 border-blue-600 dark:border-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.3)]' : 'bg-pe-sunken dark:bg-[#0a0f1c] border-blue-900/50 hover:bg-pe-hover dark:hover:bg-blue-900/20 text-blue-700 dark:text-blue-500'}`}
                                >
                                    {areAnyDataPagesVisible ? 'Arbeitsdaten ausblenden (Präsentation)' : 'Arbeitsdaten einblenden (Bearbeitung)'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Startseite;